import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Download, Search, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Logo } from "../components/Logo";
import { API, http, authHeaders, formatApiError } from "../lib/api";

const STATUSES = ["New", "Contacted", "In Progress", "Closed"];
const TOKEN_KEY = "pluk_admin_token";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await http.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data?.token ?? "");
      onLogin(data?.user ?? null);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="grid min-h-screen place-items-center px-5" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-md border border-white/10 bg-[#111519] p-8">
        <Link to="/" data-testid="admin-login-home-link"><Logo /></Link>
        <p className="font-display mt-8 text-3xl">Admin sign in</p>
        <p className="mt-1 text-sm text-zinc-400">View and manage hiring mandates.</p>
        <input type="email" required className="field mt-8" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" />
        <input type="password" required className="field mt-4" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input" />
        {error && <p className="mt-4 text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
        <button disabled={loading} className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 text-sm font-medium text-[#0a0c0e] hover:bg-emerald-400 disabled:opacity-60" data-testid="admin-login-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
        </button>
      </form>
    </div>
  );
};

const toCsv = (rows) => {
  const cols = ["created_at", "status", "name", "designation", "company", "email", "phone", "role_level", "sector", "location", "positions", "confidential", "message"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

const Detail = ({ item, onClose }) => (
  <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg bg-[#111519] border-white/10 text-zinc-100" data-testid="enquiry-detail-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-3xl font-medium">{item?.company}</DialogTitle>
      </DialogHeader>
      <dl className="grid grid-cols-3 gap-y-3 text-sm">
        {[["Contact", item?.name], ["Designation", item?.designation], ["Email", item?.email], ["Phone", item?.phone], ["Role level", item?.role_level], ["Sector", item?.sector], ["Location", item?.location], ["Positions", item?.positions], ["Confidential", item?.confidential ? "Yes" : "No"], ["Received", item?.created_at ? new Date(item.created_at).toLocaleString() : ""]].map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-zinc-400">{k}</dt>
            <dd className="col-span-2 break-words">{v || "-"}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-zinc-400 text-sm">Brief</p>
      <p className="whitespace-pre-wrap text-sm text-zinc-200">{item?.message || "-"}</p>
    </DialogContent>
  </Dialog>
);

const Dashboard = ({ user, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`${API}/enquiries`, { headers: authHeaders() });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, s) => {
    try {
      const { data } = await http.patch(`${API}/enquiries/${id}`, { status: s }, { headers: authHeaders() });
      setItems((p) => p.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked as ${s}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return items.filter((i) => (status === "All" || i.status === status) && [i.name, i.company, i.email, i.role_level, i.sector].join(" ").toLowerCase().includes(t));
  }, [items, q, status]);

  const exportCsv = () => {
    const blob = new Blob([toCsv(filtered)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pluk-mandates.csv";
    a.click();
  };

  const counts = STATUSES.map((s) => [s, items.filter((i) => i.status === s).length]);

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="admin-home-link"><Logo /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-zinc-400">{user?.email}</span>
            <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-emerald-400" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-5 py-12 sm:px-8">
        <p className="eyebrow">Admin</p>
        <h1 className="font-display mt-3 text-5xl">Hiring mandates</h1>
        <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
          {counts.map(([s, c]) => (
            <div key={s} className="rounded-md border border-white/10 bg-[#111519] p-5" data-testid={`admin-count-${s.toLowerCase().replace(" ", "-")}`}>
              <p className="text-xs text-zinc-400 font-mono uppercase tracking-[0.15em]">{s}</p>
              <p className="font-mono mt-2 text-3xl">{c}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input className="field" style={{ paddingLeft: 28 }} placeholder="Search by name, company, email, sector" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" />
          </div>
          <select className="field md:w-48" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-filter">
            {["All", ...STATUSES].map((s) => <option key={s}>{s}</option>)}
          </select>
          <button onClick={exportCsv} className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-medium text-[#0a0c0e] hover:bg-emerald-400" data-testid="admin-export-btn">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
        <div className="mt-6 overflow-x-auto rounded-md border border-white/10">
          <table className="w-full min-w-[900px] text-sm" data-testid="admin-enquiries-table">
            <thead className="bg-[#111519] text-left text-xs font-mono uppercase tracking-[0.12em] text-zinc-400">
              <tr>{["Received", "Company", "Contact", "Role level", "Sector", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-400" data-testid="admin-empty">No mandates yet.</td></tr>}
              {!loading && filtered.map((i) => (
                <tr key={i.id} className="border-t border-white/10 hover:bg-white/[0.02]" data-testid={`enquiry-row-${i.id}`}>
                  <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{new Date(i.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">{i.company}{i.confidential && <span className="ml-2 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-300">CONF</span>}</td>
                  <td className="px-4 py-3"><p>{i.name}</p><p className="text-xs text-zinc-400">{i.email}</p></td>
                  <td className="px-4 py-3">{i.role_level}</td>
                  <td className="px-4 py-3">{i.sector}</td>
                  <td className="px-4 py-3">
                    <select className="rounded border border-white/15 bg-[#111519] px-2 py-1 text-sm" value={i.status} onChange={(e) => updateStatus(i.id, e.target.value)} data-testid={`enquiry-status-select-${i.id}`}>
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setView(i)} className="text-emerald-300 hover:underline" data-testid={`enquiry-view-btn-${i.id}`}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <Detail item={view} onClose={() => setView(null)} />
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setChecking(false);
      return;
    }
    http.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => setUser(data ?? null))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  if (checking) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  return user ? <Dashboard user={user} onLogout={logout} /> : <Login onLogin={setUser} />;
}
